import { ReactNode } from "react";

import { Drawer, Toolbar, styled } from "@mui/material";

import { useAppSelector } from "app/hooks";
import {
    selectShowSidebar,
    selectInFullscreenMode,
    selectInWindowMode,
} from "./store/selectors";
import HeaderBar from "./HeaderBar";

const Main = styled("main", {
    shouldForwardProp: (prop) =>
        prop !== "sidebarIsOpen" &&
        prop !== "drawerWidth" &&
        prop !== "inFullscreenMode",
})<{
    sidebarIsOpen?: boolean;
    drawerWidth: number;
    inFullscreenMode: boolean;
}>(({ theme, sidebarIsOpen, drawerWidth, inFullscreenMode }) => ({
    flexGrow: 1,
    padding: inFullscreenMode ? theme.spacing(0) : theme.spacing(3),
    ...(inFullscreenMode && {
        maxHeight: "100vh",
        overflow: "hidden",
    }),
    transition: theme.transitions.create("margin", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: `-${drawerWidth}px`,
    ...(sidebarIsOpen && {
        transition: theme.transitions.create("margin", {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
        }),
        marginLeft: 0,
    }),
    "&::-webkit-scrollbar": { display: "none" },
    width: sidebarIsOpen ? `calc(100% - ${drawerWidth}px)` : "100%",
}));

export interface DefaultPageLayoutProps {
    drawerWidth: number;
    hideHeaderBarInFullscreen?: boolean;
    hideSideBarInFullscreen?: boolean;
    sidebar?: ReactNode;
    main?: ReactNode;
}

export function DefaultPageLayout(props: DefaultPageLayoutProps) {
    const inWindowMode = useAppSelector(selectInWindowMode);
    const inFullscreenMode = useAppSelector(selectInFullscreenMode);
    const showSidebar = useAppSelector(selectShowSidebar);

    return (
        <>
            {(inWindowMode || !props.hideHeaderBarInFullscreen) && (
                <HeaderBar drawerWidth={props.drawerWidth} />
            )}

            <Drawer
                variant={"persistent"}
                sx={{
                    width: props.drawerWidth,
                    flexShrink: 0,
                    [`& .MuiDrawer-paper`]: {
                        width: props.drawerWidth,
                        boxSizing: "border-box",
                    },
                }}
                open={
                    inFullscreenMode
                        ? !props.hideSideBarInFullscreen && showSidebar
                        : showSidebar
                }
                anchor="left"
            >
                {inWindowMode && <Toolbar />}

                {props.sidebar}
            </Drawer>
            <Main
                sidebarIsOpen={showSidebar}
                drawerWidth={props.drawerWidth}
                inFullscreenMode={inFullscreenMode}
            >
                {inWindowMode && <Toolbar />}

                {props.main}
            </Main>
        </>
    );
}
